import { JSX, useEffect, useMemo } from 'react';
import { Controller, UseFormReturn, useWatch } from 'react-hook-form';

import { Info } from 'lucide-react';

import { Checkbox } from 'components/Checkbox';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Tooltip';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { QuickQuoteInputs } from 'pages/QuoteView/Quoting/RequestQuickQuote/types';
import { resolveMaxStackHeightInches } from 'pages/QuoteView/Quoting/RequestQuickQuote/utils/ltlMetrics';

import { useCubicFeetHeightConfig } from '../hooks/useCubicFeetHeightConfig';
import { useIsSideLoadingEnabled } from '../hooks/useLTLQuickQuoteConfigFlags';

interface LTLShipmentStackingAndSideLoadControlsProps {
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
  showStackable?: boolean;
}

function renderToggleLabelWithTooltip(
  label: string,
  tooltipText: string
): JSX.Element {
  return (
    <Flex align='center' gap='xs'>
      <Typography variant='body-sm' className='leading-none text-neutral-600'>
        {label}
      </Typography>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <Info className='h-4 w-4 cursor-help text-neutral-400' />
        </TooltipTrigger>
        <TooltipContent className='mr-1 font-normal max-w-60 whitespace-pre-wrap'>
          <p>{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </Flex>
  );
}

export function LTLShipmentStackingAndSideLoadControls({
  formMethods,
  showStackable = false,
}: LTLShipmentStackingAndSideLoadControlsProps) {
  const { control, setValue } = formMethods;
  const sideLoadEnabled =
    useWatch({ control, name: 'ltl.commodity.sideLoadEnabled' }) ?? false;
  const isSideLoadingEnabled = useIsSideLoadingEnabled();
  const customConfig = useCubicFeetHeightConfig();
  const maxStackHeightInches = resolveMaxStackHeightInches(customConfig);
  const stackableTooltipText = useMemo(
    () => buildStackableTooltipText(maxStackHeightInches),
    [maxStackHeightInches]
  );

  // Split/merge is not released yet; keep form flag false until allowSplitMerge is wired.
  useEffect(() => {
    setValue('ltl.commodity.allowSplitMerge', false, {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [setValue]);

  useEffect(() => {
    if (isSideLoadingEnabled) {
      return;
    }

    setValue('ltl.commodity.sideLoadEnabled', false, {
      shouldDirty: false,
      shouldValidate: false,
    });
    setValue('ltl.commodity.allowSplitMerge', false, {
      shouldDirty: false,
      shouldValidate: false,
    });
  }, [isSideLoadingEnabled, setValue]);

  return (
    <>
      {showStackable && (
        <StackableCheckbox
          control={control}
          stackableTooltipText={stackableTooltipText}
        />
      )}

      {isSideLoadingEnabled && (
        <div className='mb-1'>
          <Controller
            name='ltl.commodity.sideLoadEnabled'
            control={control}
            render={({ field }) => (
              <Flex direction='col' gap='sm' className='w-full'>
                <Flex align='center' gap='sm'>
                  <Checkbox
                    checked={field.value ?? false}
                    onCheckedChange={(checked) => {
                      field.onChange(checked);
                      if (!checked) {
                        setValue('ltl.commodity.allowSplitMerge', false, {
                          shouldDirty: true,
                          shouldValidate: false,
                        });
                      }
                    }}
                  />
                  {renderToggleLabelWithTooltip(
                    'Side-loading',
                    'Automatically rotates commodity dimensions (length ↔ width) to find the most space-efficient trailer layout. Applied as you fill in commodity dimensions.'
                  )}
                </Flex>
                {sideLoadEnabled && <SideLoadInfoBanner />}
              </Flex>
            )}
          />
        </div>
      )}
    </>
  );
}

function buildStackableTooltipText(maxStackHeightInches: number): string {
  return `Allows pallets to be stacked vertically up to ${maxStackHeightInches} inches. Can reduce linear feet when commodities can be safely combined into stacks.`;
}

function StackableCheckbox({
  control,
  stackableTooltipText,
}: {
  control: LTLShipmentStackingAndSideLoadControlsProps['formMethods']['control'];
  stackableTooltipText: string;
}) {
  return (
    <div className='mb-1'>
      <Controller
        name='ltl.commodity.stackable'
        control={control}
        render={({ field }) => (
          <Flex align='center' gap='sm'>
            <Checkbox
              checked={field.value ?? false}
              onCheckedChange={(checked) => field.onChange(checked)}
            />
            {renderToggleLabelWithTooltip('Stackable', stackableTooltipText)}
          </Flex>
        )}
      />
    </div>
  );
}

function SideLoadInfoBanner() {
  return (
    <div className='rounded-md border border-info-500 bg-info-100 p-3'>
      <Typography variant='body-xs' className='text-neutral-700'>
        Commodity dimensions may be rotated automatically to reduce linear feet.
      </Typography>
    </div>
  );
}
